/**
 * PDF-Generator für SkyVision-Angebote.
 *
 * Vorher lebte das gesamte Layout (zwei Seiten, Logo, Positionen,
 * Summen, AGB-Footer) als ~500-Zeilen-Funktion in Summary.tsx und
 * las dutzende Werte aus dem Component-Scope. Hier ist der Aufbau
 * in benannte Sektionen zerlegt; Summary übergibt nur noch ein
 * flaches Input-Objekt.
 *
 * jsPDF bleibt ein dynamic import (webpackChunkName: "pdf-generator"),
 * damit das initiale Konfigurator-Bundle nicht um ~700 KiB wächst.
 */

import type { jsPDF } from 'jspdf';
import { companyInfo } from '../config/company';
import { MWST_RATE } from '../config/serviceConfig';
import { Dimensions, Modules } from '../types';
import {
  ACCESSORY_NAMES,
  AccessoryId,
  formatBreakdownItemName,
  formatGlassType,
} from '../utils/summaryHelpers';
import {
  calculateBasePrice,
  calculateCatalogAccessoryPrice,
  calculateDynamicAccessoryPrice,
  catalogRalSurcharge,
  matchAccessory,
} from './summaryPriceCalculations';
import { priceService, productCodeFromName } from './priceService';
import { debug } from '../utils/debug';

const PRIMARY = [119, 184, 41] as const;
const TEXT = [0, 0, 0] as const;
const LIGHT_GRAY = [128, 128, 128] as const;
const FOOTER_GRAY = [100, 100, 100] as const;
const DISCOUNT_GREEN = [0, 150, 0] as const;
const TABLE_HEADER_BG = [220, 220, 220] as const;

const LOGO_PATH =
  '/wp-content/plugins/skyvision-konfigurator/assets/glasolux-logo.png';

export interface OfferPdfCustomer {
  type: 'private' | 'business';
  firstName: string;
  lastName: string;
  street: string;
  postalCode: string;
  city: string;
  companyName?: string;
}

export interface OfferPdfDiscount {
  code: string;
  type: 'percentage' | 'fixed';
  value: number;
}

export interface OfferPdfBreakdownItem {
  item: string;
  price: number;
}

export interface OfferPdfInput {
  product: string;
  dimensions: Dimensions;
  modules: Modules;
  glassType: string | null;
  accessories: string[];
  ralColor: string;
  ralColorInside: string;
  customer: OfferPdfCustomer;
  useBackendBreakdown: boolean;
  breakdown: OfferPdfBreakdownItem[];
  netPrice: number;
  discount: OfferPdfDiscount | null;
  discountAmount: number;
}

export interface OfferPdfResult {
  pdf: jsPDF;
  offerNumber: string;
}

function formatEuro(value: number): string {
  return value.toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function buildOfferNumber(today: Date): string {
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, '0');
  const d = String(today.getDate()).padStart(2, '0');
  const rnd = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `AG${y}${m}${d}-${rnd}`;
}

async function loadLogo(): Promise<string | null> {
  try {
    const response = await fetch(LOGO_PATH);
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(typeof reader.result === 'string' ? reader.result : null);
      };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    debug('Logo konnte nicht geladen werden:', error);
    return null;
  }
}

function drawTextLogo(doc: jsPDF): void {
  doc.setFontSize(16);
  doc.setTextColor(PRIMARY[0], PRIMARY[1], PRIMARY[2]);
  doc.text('GLASOLUX', 190, 15, { align: 'right' });
  doc.setFontSize(9);
  doc.setTextColor(LIGHT_GRAY[0], LIGHT_GRAY[1], LIGHT_GRAY[2]);
  doc.text('Tageslichtsysteme', 190, 20, { align: 'right' });
}

function drawLogo(doc: jsPDF, logoImage: string | null): void {
  if (logoImage) {
    try {
      doc.addImage(logoImage, 'PNG', 140, 10, 50, 10);
      return;
    } catch {
      // Fallback auf Text, falls das PNG von jsPDF nicht gelesen wird
    }
  }
  drawTextLogo(doc);
}

function drawPageNumber(doc: jsPDF, current: number, total: number): void {
  doc.setFontSize(8);
  doc.text(`Seite ${current}/${total}`, 190, 287, { align: 'right' });
}

function renderHeader(doc: jsPDF, logoImage: string | null): void {
  drawLogo(doc, logoImage);
  doc.setFontSize(8);
  doc.setTextColor(TEXT[0], TEXT[1], TEXT[2]);
  doc.text(
    `${companyInfo.name}, ${companyInfo.street}, ${companyInfo.postalCode} ${companyInfo.city}`,
    20,
    15
  );
}

function renderCustomer(doc: jsPDF, customer: OfferPdfCustomer): number {
  let yPos = 50;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  if (customer.type === 'business' && customer.companyName) {
    doc.text(customer.companyName, 20, yPos);
    yPos += 5;
    doc.setFont('helvetica', 'normal');
    doc.text(`Herrn ${customer.firstName} ${customer.lastName}`, 20, yPos);
  } else {
    doc.text(`${customer.firstName} ${customer.lastName}`, 20, yPos);
  }
  yPos += 5;
  doc.setFont('helvetica', 'normal');
  doc.text(customer.street, 20, yPos);
  yPos += 5;
  doc.text(`${customer.postalCode} ${customer.city}`, 20, yPos);
  return yPos;
}

function renderOfferMeta(
  doc: jsPDF,
  offerNumber: string,
  today: Date
): number {
  let yPos = 85;
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Angebot', 20, yPos);

  const rightCol = 120;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');

  doc.text('Angebotsnr.:', rightCol, yPos - 5);
  doc.text(offerNumber, 155, yPos - 5);

  doc.text('Kundennr.:', rightCol, yPos);
  doc.text('15603', 155, yPos);

  doc.text('Datum:', rightCol, yPos + 5);
  doc.text(today.toLocaleDateString('de-DE'), 155, yPos + 5);

  doc.text('gültig bis:', rightCol, yPos + 10);
  const validUntil = new Date(today);
  validUntil.setDate(validUntil.getDate() + 30);
  doc.text(validUntil.toLocaleDateString('de-DE'), 155, yPos + 10);

  yPos += 20;
  doc.setFontSize(8);
  doc.text('Bearbeiterin: Online Konfigurator', 20, yPos);
  doc.text(
    'voraussichtliche Lieferzeit: 8-10 Wochen nach Freigabe und technischer Klärung',
    20,
    yPos + 4
  );
  return yPos;
}

function renderIntro(
  doc: jsPDF,
  customer: OfferPdfCustomer,
  startY: number
): number {
  let yPos = startY + 12;
  doc.setFontSize(10);
  const salutation =
    customer.type === 'business'
      ? `Sehr geehrter Herr ${customer.lastName},`
      : `Sehr geehrte/r ${customer.firstName} ${customer.lastName},`;
  doc.text(salutation, 20, yPos);

  yPos += 8;
  doc.setFontSize(9);
  const introText =
    'vielen Dank für Ihre Anfrage und das damit verbundene Interesse an unseren Produkten. Gerne unterbreiten wir Ihnen\nfreibleibend unser Angebot und freuen uns auf Ihren Auftrag.';
  const splitIntro = doc.splitTextToSize(introText, 170);
  doc.text(splitIntro, 20, yPos);
  yPos += splitIntro.length * 4;
  return yPos;
}

function renderProductDescription(doc: jsPDF, startY: number): number {
  let yPos = startY + 8;
  const productDesc =
    `Unsere GLASOLUX SkyVision-Serie ist aus organischen Plattenwerkstoff, Glas und Aluminium gefertigt. Alle Materialien\nsind recyclebar. Der Rahmen wird in der Innenleibung in fertiger weißer Oberfläche und einbaufertig geliefert. Alle\nGlasscheiben haben einen umlaufenden schwarzen Randdigitaldruck, der als UV-Schutz dient. Optional ist eine blickdichte\nOpallaminierung lieferbar. Der zum Element dazugehörige Aufsatzkranz kann bis zu einer Höhe von 750 mm gewählt\nwerden.`;
  const splitDesc = doc.splitTextToSize(productDesc, 170);
  doc.text(splitDesc, 20, yPos);
  yPos += splitDesc.length * 4;
  return yPos;
}

function renderNotices(doc: jsPDF, startY: number): void {
  let yPos = startY + 6;
  doc.setLineWidth(0.3);
  doc.line(20, yPos, 190, yPos);
  yPos += 4;
  doc.setFontSize(8);
  doc.text(
    'Positionen ohne Gesamtpreis verstehen sich als Alternativ- oder Eventualpositionen und sind im Endpreis nicht enthalten.',
    20,
    yPos
  );
  yPos += 4;
  doc.text(
    'Alle Preise verstehen sich in Euro zuzüglich der gesetzlichen Mehrwertsteuer.',
    20,
    yPos
  );
  yPos += 6;
  doc.line(20, yPos, 190, yPos);

  yPos += 6;
  doc.text(
    'Mitgeteilte Liefertermine erfolgen unter dem Vorbehalt der Selbstbelieferung.',
    20,
    yPos
  );
  yPos += 4;
  doc.text(
    'Geeignete Parkflächen (je nach Größe Sprinter/LKW/Anhänger o.ä.) sind für die Zeit der Montage (falls Montage durch',
    20,
    yPos
  );
  yPos += 4;
  doc.text(
    'GLASOLUX) bauseits zu stellen. Die Kran- und Gerüststellung erfolgt bauseits.',
    20,
    yPos
  );
  yPos += 6;
  doc.text('Ein Aufmaß ist im Angebot nicht inbegriffen.', 20, yPos);
  yPos += 4;
  doc.text(
    'Auf Wunsch ist ein Aufmaß durch unsere Mitarbeiter gegen Mehrpreis möglich.',
    20,
    yPos
  );
}

function drawProductDetails(
  doc: jsPDF,
  input: OfferPdfInput,
  startY: number
): number {
  let yPos = startY;
  doc.setFontSize(8);
  doc.setTextColor(0, 0, 0);
  if (input.product === 'SkyVision CIRCULAR') {
    doc.text(`Durchmesser: ${input.dimensions.diameter}mm`, 35, yPos);
  } else if (input.product === 'SkyVision LINEAR WALK-ON') {
    doc.text(
      `Module: ${input.modules.fixed} begehbar · ${input.dimensions.width}×${input.dimensions.length} mm`,
      35,
      yPos
    );
    yPos += 4;
    doc.text(`Höhe: ${input.dimensions.height}mm`, 35, yPos);
  } else if (input.product === 'SkyVision LINEAR') {
    doc.text(
      `Module: ${input.modules.fixed + input.modules.opening} (${input.modules.fixed} fest, ${input.modules.opening} öffenbar)`,
      35,
      yPos
    );
    yPos += 4;
    doc.text(`Höhe: ${input.dimensions.height}mm`, 35, yPos);
  } else {
    doc.text(
      `Breite: ${input.dimensions.width}mm, Länge: ${input.dimensions.length}mm, Höhe: ${input.dimensions.height}mm`,
      35,
      yPos
    );
  }
  yPos += 4;
  doc.text(`Verglasung: ${formatGlassType(input.glassType)}`, 35, yPos);
  return yPos;
}

function drawPriceColumns(
  doc: jsPDF,
  yPos: number,
  unitPrice: number
): void {
  doc.setTextColor(0, 0, 0);
  doc.text('1', 115, yPos);
  doc.text(formatEuro(unitPrice), 140, yPos, { align: 'right' });
  doc.text('-', 160, yPos, { align: 'right' });
  doc.text(formatEuro(unitPrice), 185, yPos, { align: 'right' });
}

function renderTableHeader(doc: jsPDF, yPos: number): number {
  doc.setFillColor(TABLE_HEADER_BG[0], TABLE_HEADER_BG[1], TABLE_HEADER_BG[2]);
  doc.rect(20, yPos - 5, 170, 7, 'F');

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Pos.', 22, yPos);
  doc.text('Produkt/Beschreibung', 35, yPos);
  doc.text('Mge./Ein.', 115, yPos);
  doc.text('Einzel(€)', 140, yPos, { align: 'right' });
  doc.text('Rabatt %', 160, yPos, { align: 'right' });
  doc.text('Gesamt(€)', 185, yPos, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  return yPos + 8;
}

function renderBackendPositions(
  doc: jsPDF,
  input: OfferPdfInput,
  startY: number
): number {
  let yPos = startY;
  let posNumber = 1;
  input.breakdown.forEach((item) => {
    const formattedItemName = formatBreakdownItemName(item.item, input.product);
    doc.setTextColor(0, 0, 0);
    doc.text(posNumber.toString(), 22, yPos);
    doc.setFont('helvetica', posNumber === 1 ? 'bold' : 'normal');
    doc.text(formattedItemName, 35, yPos);
    doc.setFont('helvetica', 'normal');

    if (posNumber === 1) {
      yPos += 5;
      yPos = drawProductDetails(doc, input, yPos);
      doc.setFontSize(9);
    }

    drawPriceColumns(doc, yPos, item.price);
    yPos += 8;
    posNumber++;
  });
  return yPos;
}

function renderFallbackPositions(
  doc: jsPDF,
  input: OfferPdfInput,
  startY: number
): number {
  let yPos = startY;
  const basePrice = calculateBasePrice(
    input.product,
    input.dimensions,
    input.glassType
  );

  doc.setTextColor(0, 0, 0);
  doc.text('1', 22, yPos);
  doc.setFont('helvetica', 'bold');
  doc.text(input.product, 35, yPos);
  doc.setFont('helvetica', 'normal');
  yPos += 5;

  yPos = drawProductDetails(doc, input, yPos);
  doc.setFontSize(9);
  drawPriceColumns(doc, yPos, basePrice);
  yPos += 8;

  const catalog =
    priceService.peekAccessories(productCodeFromName(input.product)) || undefined;
  const ralOutPrice = catalogRalSurcharge(catalog, 'outside');
  const ralInPrice = catalogRalSurcharge(catalog, 'inside');

  let posNumber = 2;
  if (input.ralColor && input.ralColor !== 'RAL 7043') {
    doc.setTextColor(0, 0, 0);
    doc.text(posNumber.toString(), 22, yPos);
    doc.text('RAL-Farbe Außen', 35, yPos);
    yPos += 4;
    doc.setFontSize(8);
    doc.text(input.ralColor, 35, yPos);
    doc.setFontSize(9);
    drawPriceColumns(doc, yPos, ralOutPrice);
    yPos += 8;
    posNumber++;
  }

  if (input.ralColorInside && input.ralColorInside !== 'RAL 9010') {
    doc.setTextColor(0, 0, 0);
    doc.text(posNumber.toString(), 22, yPos);
    doc.text('RAL-Farbe Innen', 35, yPos);
    yPos += 4;
    doc.setFontSize(8);
    doc.text(input.ralColorInside, 35, yPos);
    doc.setFontSize(9);
    drawPriceColumns(doc, yPos, ralInPrice);
    yPos += 8;
    posNumber++;
  }

  if (input.accessories && input.accessories.length > 0) {
    input.accessories.forEach((accessory) => {
      const catalogItem = matchAccessory(catalog, accessory);
      const price = catalogItem
        ? calculateCatalogAccessoryPrice(
            catalogItem,
            input.product,
            input.dimensions,
            basePrice,
          )
        : calculateDynamicAccessoryPrice(
            accessory,
            input.product,
            input.dimensions
          );
      if (price > 0) {
        const name = catalogItem?.name || ACCESSORY_NAMES[accessory as AccessoryId] || accessory;
        doc.setTextColor(0, 0, 0);
        doc.text(posNumber.toString(), 22, yPos);
        doc.text(name, 35, yPos);
        drawPriceColumns(doc, yPos, price);
        yPos += 8;
        posNumber++;
      }
    });
  }

  return yPos;
}

function renderPositions(
  doc: jsPDF,
  input: OfferPdfInput,
  startY: number
): number {
  if (!input.useBackendBreakdown || input.breakdown.length === 0) {
    throw new Error('Angebot ohne Backend-Preis nicht möglich.');
  }
  return renderBackendPositions(doc, input, startY);
}

function renderTotals(
  doc: jsPDF,
  input: OfferPdfInput,
  startY: number
): number {
  let yPos = startY + 5;
  doc.setLineWidth(0.3);
  doc.line(120, yPos, 190, yPos);
  yPos += 6;

  let finalNetPrice = input.netPrice;
  if (input.discount) {
    finalNetPrice -= input.discountAmount;
    doc.setTextColor(DISCOUNT_GREEN[0], DISCOUNT_GREEN[1], DISCOUNT_GREEN[2]);
    doc.text(`Rabatt (${input.discount.code})`, 115, yPos, { align: 'right' });
    doc.text(`-${formatEuro(input.discountAmount)}`, 185, yPos, {
      align: 'right',
    });
    yPos += 5;
  }

  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  doc.text('Zwischensumme (netto)', 115, yPos, { align: 'right' });
  doc.text(formatEuro(finalNetPrice), 185, yPos, { align: 'right' });
  yPos += 5;

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  const totalMwst = finalNetPrice * MWST_RATE;
  doc.text('Umsatzsteuer 19 %', 115, yPos, { align: 'right' });
  doc.text(formatEuro(totalMwst), 185, yPos, { align: 'right' });
  yPos += 6;

  doc.setLineWidth(0.5);
  doc.line(120, yPos, 190, yPos);
  yPos += 6;
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.setFillColor(TABLE_HEADER_BG[0], TABLE_HEADER_BG[1], TABLE_HEADER_BG[2]);
  doc.rect(120, yPos - 5, 70, 7, 'F');
  doc.text('Gesamtbetrag', 115, yPos, { align: 'right' });
  doc.text(formatEuro(finalNetPrice + totalMwst), 185, yPos, { align: 'right' });

  yPos += 15;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  doc.text('Zahlbar innerhalb 10 Tagen nach Rechnungsdatum', 20, yPos);
  return yPos;
}

const REMARKS = [
  'Alle Preisangaben sind in Euro und verstehen sich zuzüglich der gesetzlichen Umsatzsteuer!',
  '',
  'Wir freuen uns auf Ihre Auftragserteilung und sichern eine einwandfreie Ausführung zu.',
  '',
  'Die Preise wurden aufgrund der angefragten Menge kalkuliert und bedingen einer Gesamtabnahme.',
  'Veränderte Stückzahlen und Maße erfordern eine Neukalkulation.',
  'Die Produkte werden maßgefertigt, daher ist eine Rückgabe ausgeschlossen.',
  '',
  'Die Preise beruhen auf den angegebenen Mengen und Größen, wobei es sich hierbei um unverbindliche Richtwerte handelt.',
  'Bei Auftragserteilung werden die endgültigen Elementmaße als Länge x Breite x Höhe (LxBxH) angegeben. Daraufhin wird',
  'der endgültige Preis angegeben.',
  'Die Kran- und Gerüststellung erfolgt bauseits.',
  'Wir sind nicht für behördliche Genehmigungen/Abnahmen verantwortlich. Kosten für die behördliche Bearbeitung im',
  'Zusammenhang mit dem vorliegenden Angebot sind in diesem nicht enthalten. Stabilität und Tragfähigkeit des Unterbaus',
  'wurden nicht überprüft. Es wird vorausgesetzt, dass der Unterbau eine ebene und stabile Grundfläche bildet und für die',
  'geplanten Lasten und Kräfte berechnet ist.',
  'Bei erhöhter Innenraumfeuchtigkeit kann an der Unterseite der Oberlichter bei kalter Witterung Kondensat auftreten.',
  'Wärmestau und anfallendes Kondensat kann durch Stauraumbelüftung verhindert werden.',
  '',
  'Lieferzeit: siehe Vortext',
  'Feiertage und unvorhersehbare Ereignisse wie z.B. Glasbruch, schlechte Wetterverhältnisse u.a. können zu einer',
  'Lieferverzögerung führen, die wir nicht beeinflussen können.',
  '',
  'Zahlungsvereinbarungen: nach noch zu treffender Vereinbarung (Standard 40%/30%/30%)',
  '',
  'Bei bauseitiger Terminverschiebung nach Bestellung kommt es zu Einlagerungskosten. Diese bestehen aus einer',
  'einmaligen, mengenabhängigen Handling-Pauschale sowie einer Einlagerungspauschale pro Wochentag.',
];

function renderRemarks(
  doc: jsPDF,
  logoImage: string | null,
  startY: number
): number {
  let yPos = startY + 15;
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Sonstige Bemerkungen:', 20, yPos);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  yPos += 5;

  REMARKS.forEach((line) => {
    if (yPos > 250) {
      doc.addPage();
      drawLogo(doc, logoImage);
      yPos = 30;
      doc.setFontSize(8);
    }
    doc.setTextColor(0, 0, 0);
    doc.text(line, 20, yPos);
    yPos += 3.5;
  });
  return yPos;
}

function renderFooter(doc: jsPDF): void {
  const footerY = 270;
  doc.setFontSize(7);
  doc.setTextColor(FOOTER_GRAY[0], FOOTER_GRAY[1], FOOTER_GRAY[2]);
  doc.text(
    'Es gelten ausschließlich unsere Lieferbedingungen. Unsere AGB finden Sie im Internet unter "www.glasolux.com/agb". Alle Warenlieferungen erfolgen unter',
    20,
    footerY
  );
  doc.text(
    'Eigentumsvorbehalt. Die Ware bleibt bis zur vollständigen Bezahlung unser Eigentum.',
    20,
    footerY + 3
  );

  doc.setLineWidth(0.1);
  doc.line(20, footerY + 6, 190, footerY + 6);

  doc.setFontSize(7);
  doc.text(companyInfo.name, 20, footerY + 10);
  doc.text(`Tel. ${companyInfo.phone}`, 20, footerY + 13);
  doc.text(companyInfo.street, 20, footerY + 16);
  doc.text(`Fax ${companyInfo.fax}`, 20, footerY + 19);
  doc.text(`${companyInfo.postalCode} ${companyInfo.city}`, 20, footerY + 22);
  doc.text(companyInfo.email, 20, footerY + 25);
  doc.text('Deutschland', 20, footerY + 28);
  doc.text(companyInfo.website, 20, footerY + 31);

  doc.text(`Geschäftsführer: ${companyInfo.ceo}`, 80, footerY + 10);
  doc.text(companyInfo.courtRegistry, 80, footerY + 13);
  doc.text(`USt-IdNr.: ${companyInfo.vatId}`, 80, footerY + 16);
  doc.text(`Steuernummer: ${companyInfo.taxNumber}`, 80, footerY + 19);

  doc.text(companyInfo.banking.volksbank.name, 140, footerY + 10);
  doc.text(`IBAN: ${companyInfo.banking.volksbank.iban}`, 140, footerY + 13);
  doc.text(`BIC: ${companyInfo.banking.volksbank.bic}`, 140, footerY + 16);
  doc.text(companyInfo.banking.sparkasse.name, 140, footerY + 19);
  doc.text(`IBAN: ${companyInfo.banking.sparkasse.iban}`, 140, footerY + 22);
  doc.text(`BIC: ${companyInfo.banking.sparkasse.bic}`, 140, footerY + 25);
}

/**
 * Erzeugt das zweiseitige Angebots-PDF. Der Browser lädt jsPDF erst
 * hier on-demand; weitere Aufrufe kommen aus dem Cache.
 */
export async function generateOfferPdf(
  input: OfferPdfInput
): Promise<OfferPdfResult> {
  const { jsPDF } = await import(
    /* webpackChunkName: "pdf-generator" */ 'jspdf'
  );
  const doc = new jsPDF();
  const today = new Date();
  const offerNumber = buildOfferNumber(today);
  const logoImage = await loadLogo();

  // Seite 1: Briefkopf, Anrede, Produktbeschreibung, Hinweise
  renderHeader(doc, logoImage);
  renderCustomer(doc, input.customer);
  const afterMetaY = renderOfferMeta(doc, offerNumber, today);
  const afterIntroY = renderIntro(doc, input.customer, afterMetaY);
  const afterDescY = renderProductDescription(doc, afterIntroY);
  renderNotices(doc, afterDescY);
  drawPageNumber(doc, 1, 2);

  // Seite 2: Positionen, Summen, Bemerkungen, Firmenfuß
  doc.addPage();
  drawLogo(doc, logoImage);
  doc.setFontSize(10);
  doc.setTextColor(TEXT[0], TEXT[1], TEXT[2]);
  doc.text(`Angebot ${offerNumber}`, 20, 30);

  const afterHeaderY = renderTableHeader(doc, 45);
  const afterPositionsY = renderPositions(doc, input, afterHeaderY);
  const afterTotalsY = renderTotals(doc, input, afterPositionsY);
  renderRemarks(doc, logoImage, afterTotalsY);
  renderFooter(doc);
  drawPageNumber(doc, 2, 2);

  return { pdf: doc, offerNumber };
}
